import axios, { AxiosInstance } from 'axios'

type DjangoAuthData = {
  access_token: string
  expires_in: number
  token_type: 'Bearer' | string
  scope: string
  refresh_token: string
}

type DjangoAuthOkResponse = {
  code: string
  error: false
  message: string
  data: DjangoAuthData
  error_data: Record<string, unknown>
}

type DjangoAuthErrorResponse = {
  code: string
  error: true
  message: string
  data: null
  error_data: { error?: string } | Record<string, unknown>
}

type DjangoAuthResponse = DjangoAuthOkResponse | DjangoAuthErrorResponse

const {
  REACT_APP_URL_PATH_API_DJANGO,
  REACT_APP_CLIENT_ID_API_DJANGO,
  REACT_APP_CLIENT_SECRET_API_DJANGO,
} = process.env as Record<string, string | undefined>

const STORAGE_KEY = 'django_oauth'

export const apiDjango: AxiosInstance = axios.create({
  baseURL: REACT_APP_URL_PATH_API_DJANGO,
  timeout: 30000,
})

// El backend Django localiza sus respuestas (mensajes y errores) según ?lang=<código>.
// Lo inyectamos en TODA petición para recibir el texto ya en español y no traducir en
// el front. Se mergea con los params existentes; va como default, así un caller podría
// sobrescribir `lang` puntualmente si alguna vez hiciera falta otro idioma.
apiDjango.interceptors.request.use((config) => {
  // `params` puede venir como objeto plano, URLSearchParams o string. El spread solo es
  // seguro para objetos planos (spread-ear un URLSearchParams/string descarta o corrompe
  // los params existentes). `lang=es` va como default y nunca pisa un `lang` ya puesto
  // por el caller.
  const params = config.params
  if (params instanceof URLSearchParams) {
    if (!params.has('lang')) params.set('lang', 'es')
  } else if (typeof params === 'string') {
    if (!/(^|&)lang=/.test(params))
      config.params = params ? `${params}&lang=es` : 'lang=es'
  } else {
    config.params = { lang: 'es', ...(params || {}) }
  }
  return config
})

function setAuth(data: DjangoAuthData) {
  const expiresAt = Date.now() + data.expires_in * 1000
  const saved = { ...data, expires_at: expiresAt }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(saved))
  } catch {}
  apiDjango.defaults.headers.common.Authorization = `${data.token_type} ${data.access_token}`
}

export function clearDjangoAuth() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {}
  delete apiDjango.defaults.headers.common.Authorization
}

export function restoreDjangoAuthFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as DjangoAuthData & { expires_at?: number }
    if (!parsed.access_token || !parsed.expires_at) return
    if (Date.now() >= parsed.expires_at) {
      clearDjangoAuth()
      return
    }
    apiDjango.defaults.headers.common.Authorization = `${parsed.token_type} ${parsed.access_token}`
  } catch {
    clearDjangoAuth()
  }
}

export async function loginDjango(
  username: string,
  password: string
): Promise<DjangoAuthData> {
  if (!REACT_APP_CLIENT_ID_API_DJANGO || !REACT_APP_CLIENT_SECRET_API_DJANGO) {
    throw new Error('Faltan CLIENT_ID o CLIENT_SECRET en .env')
  }

  const body = new URLSearchParams()
  body.append('grant_type', 'password')
  body.append('username', username)
  body.append('password', password)

  const basicAuth =
    'Basic ' +
    btoa(
      `${REACT_APP_CLIENT_ID_API_DJANGO}:${REACT_APP_CLIENT_SECRET_API_DJANGO}`
    )

  const resp = await apiDjango.post<DjangoAuthResponse>(
    '/auth/get-token',
    body.toString(),
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: basicAuth,
      },
    }
  )

  if (
    resp.status === 200 &&
    resp.data &&
    (resp.data as DjangoAuthOkResponse).error === false
  ) {
    const data = (resp.data as DjangoAuthOkResponse).data as DjangoAuthData
    setAuth(data)
    return data
  }

  const message = String(
    (resp.data as any)?.message ??
      (resp.data as any)?.error_data?.error ??
      'Error autenticando contra API Django'
  )
  throw new Error(message)
}

export function hasValidDjangoToken(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return false
    const parsed = JSON.parse(raw) as { expires_at?: number }
    return !!parsed.expires_at && Date.now() < (parsed.expires_at as number)
  } catch {
    return false
  }
}
